const PIXEL_RATIO = window.devicePixelRatio || 1

/**
 * 座標Aと座標Bのピクセル距離を返す
 */
const pixelDistance = (a, b) => {
  return Math.sqrt(Math.pow(a.left - b.left, 2) + Math.pow(a.top - b.top, 2))
}

/**
 * マーカー画像を読込み
 */
const createMarker = (src) => {
  return new Promise((resolve) => {
    const img = document.createElement('img')
    img.onload = () => {
      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')
      context.scale(PIXEL_RATIO, PIXEL_RATIO)
      canvas.width = TileDrawer.MARKER_WIDTH * PIXEL_RATIO
      canvas.height = TileDrawer.MARKER_HEIGHT * PIXEL_RATIO
      context.drawImage(img, 0, 0, TileDrawer.MARKER_WIDTH * PIXEL_RATIO, TileDrawer.MARKER_HEIGHT * PIXEL_RATIO)
      resolve(canvas)
    }
    img.width = TileDrawer.MARKER_WIDTH * PIXEL_RATIO
    img.height = TileDrawer.MARKER_HEIGHT * PIXEL_RATIO
    img.src = src
  })
}

/**
 * 地図を分割したタイル上にroomGroupプロットを描画するクラス
 * <canvas>を用いて描画
 */
export default class TileDrawer {
  static MARKER_WIDTH = 40
  static MARKER_HEIGHT = 46
  static MARKER = createMarker('/assets/image/search/map/marker.svg')

  // 描画中のマーカー
  currentMarkers = new Map()

  /**
   * divを生成
   */
  get div() {
    if (this._div) { return this._div }
    const div = document.createElement('div')
    div.classList.add('vs-map-tile')
    return (this._div = div)
  }

  /**
   * canvasを生成
   */
  get canvas() {
    if (this._canvas) { return this._canvas }
    const canvas = document.createElement('canvas')
    canvas.getContext('2d').scale(PIXEL_RATIO, PIXEL_RATIO)
    return (this._canvas = canvas)
  }

  /**
   * canvasの2Dコンテキスト
   */
  get context() {
    return this.canvas.getContext('2d')
  }

  /**
   * divとcanvasの位置・サイズを設定
   * divのサイズはタイルと一致、canvasはマーカーサイズ分拡張した大きさ
   */
  setDimension({ left, top, width, height }) {
    this.div.style.position = 'absolute'
    this.div.style.left = `${left}px`
    this.div.style.top = `${top}px`
    this.div.style.width = `${width}px`
    this.div.style.height = `${height}px`

    // タイル境界のデバッグ時は下記をコメントイン
    // this.div.style.background = 'rgba(0, 0, 0, .3)'
    // this.div.style.boxSizing = 'border-box'
    // this.div.style.border = '1px solid white'

    // タイル境界上のマーカーを描くため、canvasのサイズはマーカー分のmarginを付与
    // +2 は、shadowの描画分
    const canvasWidth = width + TileDrawer.MARKER_WIDTH + 2
    const canvasHeight = height + TileDrawer.MARKER_HEIGHT + 2

    this.canvas.style.position = 'absolute'
    this.canvas.style.left = `-${TileDrawer.MARKER_WIDTH / 2}px`
    this.canvas.style.top = `-${TileDrawer.MARKER_HEIGHT}px`
    this.canvas.style.width = `${canvasWidth}px`
    this.canvas.style.height = `${canvasHeight}px`
    this.canvas.width = canvasWidth * PIXEL_RATIO
    this.canvas.height = canvasHeight * PIXEL_RATIO
  }

  isContainedTo(container) {
    return this.div.parentElement == container
  }

  appendTo(container) {
    container.appendChild(this.div)
  }

  /**
   * マーカーを描画
   */
  async plot(coords, roomGroup) {
    const { left, top } = coords
    const { MARKER, MARKER_WIDTH, MARKER_HEIGHT } = TileDrawer

    // <canvas>の代わりに<div>をプロットする場合は下記をコメントイン
    // const div = document.createElement('div')
    // div.style.position = 'absolute'
    // div.style.width = `${MARKER_WIDTH}px`
    // div.style.height = `${MARKER_WIDTH}px`
    // div.style.borderRadius = `${MARKER_WIDTH / 2}px`
    // div.style.backgroundColor = 'rgba(255, 0, 0, .5)'
    // div.style.left = `${left - MARKER_WIDTH / 2}px`
    // div.style.top = `${top - MARKER_WIDTH / 2}px`
    // this.div.appendChild(div)

    const img = await MARKER

    // canvasに描画
    // this.context.shadowColor = 'rgba(0, 0, 0, .3)'
    // this.context.shadowOffsetX = 2 * PIXEL_RATIO
    // this.context.shadowOffsetY = 2 * PIXEL_RATIO
    // this.context.shadowBlur = 4 * PIXEL_RATIO
    this.context.drawImage(img, Math.round(left * PIXEL_RATIO), Math.round(top * PIXEL_RATIO))

    // マーカーとマウスの衝突判定をする円の中心点の座標
    const collisionCircleCenter = {
      left,
      top: top - (MARKER_WIDTH / 2) - (MARKER_HEIGHT - MARKER_WIDTH)
    }

    // マーカーの位置とroomGroupの紐づけを保持
    this.currentMarkers.set(collisionCircleCenter, roomGroup)
  }

  /**
   * <canvas>の描画を消去
   */
  clear() {
    // this.context.clearRect(0, 0, this.canvas.width, this.canvas.height)
    this.currentMarkers.clear()
  }

  /**
   *
   */
  draw() {
    this.div.appendChild(this.canvas)
  }

  /**
   * マウスの位置がマーカー半径内にあるroomGroupを返す
   */
  hits(mouse) {
    return [...this.currentMarkers].map(([marker, roomGroup]) => (
      [pixelDistance(mouse, marker), roomGroup]
    )).filter(([distance]) => (
      distance <= TileDrawer.MARKER_WIDTH / 2
    ))
  }

  /**
   * div, canvasを破棄
   */
  remove() {
    this.clear()

    // @see https://qiita.com/minimo/items/b724c6793f45aca5e6f5
    this.canvas.width = 0
    this.canvas.height = 0

    this.div.parentElement && this.div.parentElement.removeChild(this.div)
    this.canvas.parentElement && this.canvas.parentElement.removeChild(this.canvas)
    delete this._div
    delete this._canvas
  }
}
